import tw, { styled, css } from 'twin.macro';

export const DefaultCheckboxLabel = styled.label(() => [
  tw`inline-flex items-center py-4 cursor-pointer break-words min-w-0`,

  /* active label
   * when the label text is clicked, prevent the checkbox input to loose the focus style
   */
  css`
    &:active > input {
      ${tw`border-blue-base`};
    }
  `
]);

export const DefaultCheckboxInput = styled.input(({ disabled }) => [
  /* default checkbox */
  `margin: 0.0625rem 0;`,
  tw`p-16 cursor-pointer`,
  disabled && [tw`border-silver-lighten-15 bg-silver-lighten-15 cursor checked:(border-silver-lighten-15 bg-silver-lighten-15 cursor)`, `background-blend-mode: luminosity;`]
]);

export const BaseCheckbox = styled.div.attrs(({ variant }) => ({
  dataVariant: variant
}))(({ disabled }) => [
  tw`relative inline-block outline-none align-baseline text-lg cursor-pointer leading-none`,
  css`
    min-width: 1rem;
    min-height: 1rem;
  `,
  css`
    & input[type='checkbox'],
    & input[type='radio'] {
      ${tw`absolute cursor-pointer top-0 left-0 opacity-0 outline-none w-16 h-16`};
      z-index: 3;
    }
  `,

  /*
     * [1] Provide a fallback style for browsers
   that don't support :focus-visible
     *  [2] Remove the focus indicator on mouse-focus for browsers
     that do support :focus-visible
     *  [3] Draw a very noticeable focus style for
     keyboard-focus on browsers that do support
     :focus-visible
  */
  css`
    & label {
      ${tw`cursor-pointer relative block text-gray-darken-30 text-black text-opacity-85`}
    }
    & label:before {
      ${tw`absolute top-0 left-0 bg-white rounded border border-solid border-silver-darken-30 w-16 h-16`};
      content: '';
    }
    @media (hover: hover) {
      & input:hover ~ label:before {
        ${tw`border-gray-lighten-20`};
      }
    }
    & input:active ~ label:before {
      ${tw`border-blue-base`};
    }
    & input:checked ~ label:before {
      ${tw`border-gray-lighten-30`};
    }
    & input:focus ~ label:before {
      ${tw`ring ring-blue-lighten-20`}; /* [1] */
    }
    & input:focus:not(:focus-visible) ~ label:before {
      ${tw`ring-0`}; /* [2] */
    }
    & input:focus-visible ~ label:before {
      ${tw`ring ring-blue-lighten-20`}; /* [3] */
    }
    & input:checked:focus ~ label:before {
      ${tw`ring ring-blue-lighten-20`}; /* [1] */
    }
    & input:checked:focus:not(:focus-visible) ~ label:before {
      ${tw`ring-0`}; /* [2] */
    }
    & input:checked:focus-visible ~ label:before {
      ${tw`ring ring-blue-lighten-20`}; /* [3] */
    }
    &[data-variant='default'] input:checked ~ label > .checkIcon {
      ${tw`opacity-100`}
    }
  `,
  disabled &&
    css`
      ${tw`pointer-events-none opacity-60 outline-none`}

      & input ~ label:before {
        ${tw`bg-silver-lighten-20`};
      }
    `
]);

export const RoundedCheckbox = styled(BaseCheckbox)(({ disabled }) => [
  css`
    & label:before {
      ${tw`bg-white border border-solid border-blue-base rounded w-16 h-16 opacity-100`};
      border-radius: 1rem;
      transition: border 150ms ease, opacity 0.1s linear;
    }
    @media (hover: hover) {
      &:hover label:before {
        ${tw`border-blue-base`};
        border-width: 0.375rem;
      }
    }

    &:active label:before {
      ${tw`border-blue-base`};
      opacity: 0.4;
    }

    & input:checked ~ label:before {
      ${tw`border-blue-base`};
      border-width: 0.375rem;
    }
    @media (hover: hover) {
      & input:hover ~ label:before {
        ${tw`border-blue-base`};
      }

      &:hover input:checked ~ label:before {
        ${tw`border-blue-base`};
        border-width: 0.375rem;
      }
    }
  `,
  disabled &&
    css`
      & label:before,
      & input:checked ~ label:before {
        ${tw`border-gray-base`};
      }
    `
]);

export const SwitchCheckbox = styled(BaseCheckbox)(() => [
  css`
    min-height: 1.75rem;
    ${tw`py-8`}

    & input[type='checkbox'] {
      width: 3.5rem;
      height: 1.75rem;
    }

    & label {
      min-height: 1.75rem;
      padding-left: 3.5rem;
    }

    & label:before {
      ${tw`block absolute border-none z-1 top-0 bg-silver-lighten-20 text-black text-opacity-85 font-bold text-right`}
      content: 'OFF';
      line-height: 1.75rem;
      width: 3.5rem;
      padding-right: 0.35rem;
      height: 1.75rem;
      border-radius: 500rem;
      font-size: 0.7rem;
    }

    & label:after {
      ${tw`absolute opacity-100 border-none`}
      top: 0.125rem;
      left: 0.125rem;
      background: #fff linear-gradient(transparent, rgba(0, 0, 0, 0.05));
      content: '';
      z-index: 2;
      box-shadow: 0 1px 2px 0 rgba(34, 36, 38, 0.15),
        0 0 0 1px rgba(34, 36, 38, 0.15) inset;
      width: 1.5rem;
      height: 1.5rem;
      border-radius: 500rem;
      transition: background 0.3s ease, left 0.3s ease;
    }

    & input ~ label:after {
      box-shadow: 0 1px 2px 0 rgba(34, 36, 38, 0.15),
        0 0 0 1px rgba(34, 36, 38, 0.15) inset;
    }

    & input:checked ~ label:before {
      ${tw`bg-blue-base text-left text-white pl-8`}
      content: 'ON';
    }

    & input:checked ~ label:after {
      top: 0.125rem;
      left: 1.9rem;
      box-shadow: 0 1px 2px 0 rgba(34, 36, 38, 0.15),
        0 0 0 1px rgba(34, 36, 38, 0.15) inset;
    }
  `
]);
